import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import type { ChatMessage, ChatRoom } from '../../types/chat';
import MessageAttachments from './MessageAttachments';
import CardMessage from './CardMessage';
import { parseApiDate } from '../../utils/apiDates';

interface MessageBubbleProps {
  message: ChatMessage;
  isSelf: boolean;
  canEdit?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
  // Used to compute Sent/Seen ticks on self-bubbles. ULIDs are
  // lexicographically time-sortable, so a string `>=` against each peer's
  // last_read_message_id is the seen check.
  room?: ChatRoom;
  selfId?: string | null;
}

interface ReadStatus {
  // 'sent' (single ✓) or 'seen' (double ✓✓). Group rooms also report partial.
  state: 'sent' | 'seen';
  partial?: { seen: number; total: number };
}

function computeReadStatus(
  message: ChatMessage,
  room: ChatRoom | undefined,
  selfId: string | null | undefined
): ReadStatus {
  if (!room || !selfId) return { state: 'sent' };
  const peers = room.members.filter((m) => m.user_id !== selfId);
  if (peers.length === 0) return { state: 'sent' };
  const seen = peers.filter(
    (m) => !!m.last_read_message_id && m.last_read_message_id >= message.id
  ).length;
  if (seen === peers.length) return { state: 'seen' };
  if (seen === 0) return { state: 'sent' };
  return { state: 'sent', partial: { seen, total: peers.length } };
}

const ALLOWED_TAGS = ['b', 'i', 'em', 'strong', 'a', 'br', 'p', 'span'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isSelf,
  canEdit,
  onEdit,
  onDelete,
  className,
  room,
  selfId,
}) => {
  const [hovered, setHovered] = useState(false);
  const isDeleted = !!message.deleted_at;
  const isEdited = !!message.edited_at && !isDeleted;
  const sendError = message.send_error;
  const showTicks = isSelf && !isDeleted && !message.pending && !sendError;
  const readStatus = showTicks ? computeReadStatus(message, room, selfId) : null;

  // Rich invitation card — rendered instead of a text/file bubble.
  if (!isDeleted && message.kind === 'card' && message.payload) {
    return (
      <div className={`group flex flex-col ${isSelf ? 'items-end' : 'items-start'} ${className ?? ''}`}>
        <CardMessage payload={message.payload} />
        <div className="flex items-center gap-1.5 mt-1 px-1 text-[11px] text-text-tertiary">
          <span>{format(parseApiDate(message.created_at), 'p')}</span>
        </div>
      </div>
    );
  }

  const bubbleClass = isSelf
    ? 'bg-primary-500 text-white'
    : 'bg-neutral-100 dark:bg-neutral-800 text-text-primary';
  const align = isSelf ? 'items-end' : 'items-start';
  const hasBody = !isDeleted && !!message.body && message.body.trim().length > 0;
  const hasAttachments = !isDeleted && message.attachment_ids.length > 0;
  const pendingClass = message.pending ? 'opacity-70' : '';

  return (
    <div
      className={`group flex flex-col ${align} ${className ?? ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isDeleted && (
        <div className={`relative max-w-[420px] rounded-2xl px-3.5 py-2 ${bubbleClass}`}>
          <p className="italic text-sm opacity-75">[deleted]</p>
        </div>
      )}
      {hasBody && (
        <div className={`relative max-w-[420px] rounded-2xl px-3.5 py-2 ${bubbleClass} ${pendingClass}`}>
          <div
            className="text-sm whitespace-pre-wrap break-words"
            dangerouslySetInnerHTML={{ __html: sanitize(message.body ?? '') }}
          />
        </div>
      )}
      {hasAttachments && (
        <div className={`max-w-[420px] ${hasBody ? 'mt-1' : ''} ${pendingClass}`}>
          <MessageAttachments
            roomId={message.room_id}
            attachmentIds={message.attachment_ids}
          />
        </div>
      )}

      <div className="flex items-center gap-1.5 mt-1 px-1 text-[11px] text-text-tertiary">
        <span>{format(parseApiDate(message.created_at), 'p')}</span>
        {isEdited && <span className="italic">(edited)</span>}
        {message.pending && <span className="italic">Sending…</span>}
        {sendError && <span className="text-error-600">Failed to send</span>}
        {readStatus && (
          <span
            className={`flex items-center ${
              readStatus.state === 'seen' ? 'text-primary-500' : 'text-text-tertiary'
            }`}
            title={
              readStatus.state === 'seen'
                ? 'Seen'
                : readStatus.partial
                  ? `Seen by ${readStatus.partial.seen}/${readStatus.partial.total}`
                  : 'Sent'
            }
          >
            <CheckIcon className="w-3.5 h-3.5" />
            {readStatus.state === 'seen' && (
              <CheckIcon className="w-3.5 h-3.5 -ml-2" />
            )}
            {readStatus.partial && (
              <span className="ml-1">
                {readStatus.partial.seen}/{readStatus.partial.total}
              </span>
            )}
          </span>
        )}
      </div>

      {!isDeleted && hovered && !message.pending && (
        <div
          className={`flex items-center gap-1 mt-0.5 ${
            isSelf ? 'flex-row-reverse' : ''
          }`}
        >
          {canEdit && onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="p-1.5 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label="Edit"
            >
              <PencilIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {canEdit && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 rounded-full text-text-tertiary hover:text-error-600 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label="Delete"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
